import React, { useEffect, useState } from "react";
import { useLocation, useHistory, NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import SidebarRoot from "./SidebarRoot";

// Icons
import DashboardIcon from "@mui/icons-material/Dashboard";
import HomeIcon from "@mui/icons-material/Home";
import CameraIcon from "@mui/icons-material/Camera";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Group from "@mui/icons-material/Group";
import PersonAdd from "@mui/icons-material/PersonAdd";
import Category from "@mui/icons-material/Category";
import HardwareIcon from "@mui/icons-material/Memory";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import InfoIcon from "@mui/icons-material/Info";
import BarChartIcon from "@mui/icons-material/BarChart";

export default function Sidebar({
  brand = "",
  brandName,
  miniSidenav = false,
  setMiniSidenav,
  darkMode = false,
  setDarkMode,
  ...rest
}) {
  const location = useLocation();
  const history = useHistory();
  const { pathname } = location;
  const [currentSection, setCurrentSection] = useState(null);
  const [openDropdowns, setOpenDropdowns] = useState({
    "user-management": false,
    "home-sections": false,
  });

  // Track scroll position to highlight home sections
  useEffect(() => {
    if (pathname === "/home") {
      const handleScroll = () => {
        const sections = [
          "introduction",
          "stats",
          "species-count",
          "hardware",
          "species-info",
          "teams",
          "features",
          "quick",
        ];

        for (const section of sections) {
          const element = document.getElementById(section);
          if (element) {
            const rect = element.getBoundingClientRect();
            if (rect.top <= 100 && rect.bottom >= 100) {
              setCurrentSection(section);
              break;
            }
          }
        }
      };

      window.addEventListener("scroll", handleScroll);
      // Initial check
      setTimeout(handleScroll, 100);

      return () => window.removeEventListener("scroll", handleScroll);
    } else {
      setCurrentSection(null);
    }
  }, [pathname]);

  const toggleDropdown = (key, e) => {
    // Prevent navigation when clicking on dropdown toggle
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setOpenDropdowns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  useEffect(() => {
    // Set dropdown open if current path is in user-management
    if (pathname.includes("/user-management")) {
      setOpenDropdowns((prev) => ({
        ...prev,
        "user-management": true,
      }));
    } else {
      // Close user management dropdown when not on user management
      setOpenDropdowns((prev) => ({
        ...prev,
        "user-management": false,
      }));
    }

    // Close home sections dropdown when not on home page
    if (pathname !== "/home") {
      setOpenDropdowns((prev) => ({
        ...prev,
        "home-sections": false,
      }));
    }

    // Handle window resize
    function handleMiniSidenav() {
      if (window.innerWidth < 1200) {
        setMiniSidenav(true);
      }
    }

    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();

    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [setMiniSidenav, pathname]);

  const getUserType = () => {
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.user_type;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const userType = getUserType();

  // Only show User Management to admin and superuser
  const showUserManagement = userType === "admin" || userType === "superuser";

  const routes = [
    {
      type: "collapse",
      name: "Home",
      key: "home",
      route: "/home",
      icon: <HomeIcon />,
      noCollapse: false,
      children: [
        {
          name: "Introduction",
          key: "introduction",
          route: "/home",
          icon: <InfoIcon />,
          sectionId: "introduction",
          isDefault: true, // Mark as default section
        },
        {
          name: "System Overview",
          key: "stats",
          route: "/home#stats",
          icon: <BarChartIcon />,
          sectionId: "stats",
        },
        {
          name: "Species Detected",
          key: "species-count",
          route: "/home#species-count",
          icon: <Category />,
          sectionId: "species-count",
        },
        {
          name: "Hardware",
          key: "hardware",
          route: "/home#hardware",
          icon: <HardwareIcon />,
          sectionId: "hardware",
        },
        {
          name: "Species Info",
          key: "species-info",
          route: "/home#species-info",
          icon: <CameraIcon />,
          sectionId: "species-info",
        },
        {
          name: "Team & Collaborators",
          key: "teams",
          route: "/home#teams",
          icon: <GroupWorkIcon />,
          sectionId: "teams",
        },
        {
          name: "Features",
          key: "features",
          route: "/home#features",
          icon: <DashboardIcon />,
          sectionId: "features",
        },
        {
          name: "Quick Actions",
          key: "quick",
          route: "/home#quick",
          icon: <CameraIcon />,
          sectionId: "quick",
        },
      ],
    },
    {
      type: "collapse",
      name: "Dashboard",
      key: "dashboard",
      route: "/dashboard",
      icon: <DashboardIcon />,
      noCollapse: true,
    },
    {
      type: "collapse",
      name: "Cameras",
      key: "cameras",
      route: "/cameras",
      icon: <CameraIcon />,
      noCollapse: true,
    },
  ];

  // Add User Management only if user has permission
  if (showUserManagement) {
    routes.push({
      type: "collapse",
      name: "User Management",
      key: "user-management",
      route: "#",
      icon: <PeopleIcon />,
      noCollapse: false,
      children: [
        {
          name: "All Users",
          key: "all-users",
          route: "/user-management/all-users",
          icon: <Group />,
        },
        {
          name: "Add New User",
          key: "add-user",
          route: "/user-management/add-user",
          icon: <PersonAdd />,
        },
        {
          name: "Species",
          key: "species",
          route: "/user-management/species",
          icon: <Category />,
        },
      ],
    });
  }

  // Add Profile for everyone
  routes.push({
    type: "collapse",
    name: "Profile",
    key: "profile",
    route: "/profile",
    icon: <PersonIcon />,
    noCollapse: true,
  });

  // Gradient styles matching TopAppBar
  const gradientBackground = darkMode
    ? "linear-gradient(180deg, #1a1a1a 0%, #2d2d2d 100%)"
    : "linear-gradient(180deg, #454545 0%, rgba(90, 90, 90, 0.98) 100%)";

  // Helper to check if a section is active
  const isSectionActive = (sectionId) => {
    return pathname === "/home" && currentSection === sectionId;
  };

  const handleHomeClick = (e) => {
    const topAppBarHeight = 70;
    // Only navigate if not already on home
    if (pathname !== "/home") {
      history.push("/home");
      // Scroll to introduction after navigation
      setTimeout(() => {
        const element = document.getElementById("introduction");
        if (element) {
          const y =
            element.getBoundingClientRect().top +
            window.pageYOffset -
            topAppBarHeight;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 100);
    }
    // If already on home, do nothing (stay on current section)
  };

  // Handle section click from dropdown
  const handleSectionClick = (e, child) => {
    e.preventDefault();
    e.stopPropagation();

    const topAppBarHeight = 70; // Height of TopAppBar

    if (child.isDefault) {
      // For Introduction, navigate to /home and scroll to introduction
      if (pathname === "/home") {
        // If already on home, just scroll to introduction with offset
        const element = document.getElementById("introduction");
        if (element) {
          const y =
            element.getBoundingClientRect().top +
            window.pageYOffset -
            topAppBarHeight;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      } else {
        // If not on home, navigate to home and then scroll to introduction
        history.push("/home");
        // Use setTimeout to ensure the page loads before scrolling
        setTimeout(() => {
          const element = document.getElementById("introduction");
          if (element) {
            const y =
              element.getBoundingClientRect().top +
              window.pageYOffset -
              topAppBarHeight;
            window.scrollTo({ top: y, behavior: "smooth" });
          }
        }, 100);
      }
    } else {
      // For other sections, scroll to the section with offset
      const element = document.getElementById(child.sectionId);
      if (element) {
        const y =
          element.getBoundingClientRect().top +
          window.pageYOffset -
          topAppBarHeight;
        window.scrollTo({ top: y, behavior: "smooth" });
        // Update URL hash without reload
        window.history.pushState({}, "", child.route);
      }
    }
  };

  const renderRoutes = routes.map(
    ({ type, name, icon, route, key, noCollapse, children }) => {
      if (type === "collapse") {
        if (children) {
          // Check if home is active (on home page)
          const isHomeActive = pathname === "/home";
          const isOpen = openDropdowns[key];

          // If it's the Home dropdown
          if (key === "home") {
            return (
              <React.Fragment key={key}>
                <ListItem
                  button
                  onClick={handleHomeClick}
                  sx={{
                    minHeight: 48,
                    borderRadius: "12px",
                    margin: "4px 10px",
                    padding: "10px 14px",
                    backgroundColor: isHomeActive
                      ? "rgba(255,255,255,0.15)"
                      : "transparent",
                    color: isHomeActive ? "#ffffff" : "rgba(255,255,255,0.8)",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                      transform: "translateX(4px)",
                      transition: "all 0.3s ease",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isHomeActive ? "#ffffff" : "rgba(255,255,255,0.7)",
                      minWidth: miniSidenav ? "auto" : 40,
                    }}
                  >
                    {icon}
                  </ListItemIcon>
                  {!miniSidenav && (
                    <>
                      <ListItemText
                        primary={name}
                        primaryTypographyProps={{
                          fontSize: "0.875rem",
                          fontWeight: isHomeActive ? 600 : 400,
                          color: isHomeActive
                            ? "#ffffff"
                            : "rgba(255,255,255,0.85)",
                        }}
                      />
                      <Box
                        onClick={(e) => toggleDropdown(key, e)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.1)",
                            borderRadius: "4px",
                          },
                        }}
                      >
                        {isOpen ? (
                          <ExpandLess sx={{ color: "rgba(255,255,255,0.6)" }} />
                        ) : (
                          <ExpandMore sx={{ color: "rgba(255,255,255,0.6)" }} />
                        )}
                      </Box>
                    </>
                  )}
                </ListItem>

                {!miniSidenav && (
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {children.map((child) => {
                        const isChildActive = child.sectionId
                          ? isSectionActive(child.sectionId)
                          : pathname === child.route;

                        return (
                          <ListItem
                            key={child.key}
                            button
                            onClick={(e) => handleSectionClick(e, child)}
                            sx={{
                              minHeight: 40,
                              pl: 4,
                              margin: "2px 10px",
                              borderRadius: "10px",
                              backgroundColor: isChildActive
                                ? "rgba(255,255,255,0.12)"
                                : "transparent",
                              "&:hover": {
                                backgroundColor: "rgba(255,255,255,0.08)",
                                transform: "translateX(4px)",
                                transition: "all 0.3s ease",
                              },
                              transition: "all 0.3s ease",
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 36,
                                color: isChildActive
                                  ? "#ffffff"
                                  : "rgba(255,255,255,0.6)",
                              }}
                            >
                              {child.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={child.name}
                              primaryTypographyProps={{
                                fontSize: "0.875rem",
                                fontWeight: isChildActive ? 600 : 400,
                                color: isChildActive
                                  ? "#ffffff"
                                  : "rgba(255,255,255,0.75)",
                              }}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          }

          // For other dropdowns (User Management)
          const isParentActive = children.some(
            (child) => pathname === child.route,
          );
          const isUserMgmtOpen = openDropdowns[key];

          return (
            <React.Fragment key={key}>
              <ListItem
                button
                onClick={(e) => toggleDropdown(key, e)}
                sx={{
                  minHeight: 48,
                  borderRadius: "12px",
                  margin: "4px 10px",
                  padding: "10px 14px",
                  backgroundColor: isParentActive
                    ? "rgba(255,255,255,0.15)"
                    : "transparent",
                  color: isParentActive ? "#ffffff" : "rgba(255,255,255,0.8)",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                    transition: "all 0.3s ease",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isParentActive ? "#ffffff" : "rgba(255,255,255,0.7)",
                    minWidth: miniSidenav ? "auto" : 40,
                  }}
                >
                  {icon}
                </ListItemIcon>
                {!miniSidenav && (
                  <>
                    <ListItemText
                      primary={name}
                      primaryTypographyProps={{
                        fontSize: "0.875rem",
                        fontWeight: isParentActive ? 600 : 400,
                        color: isParentActive
                          ? "#ffffff"
                          : "rgba(255,255,255,0.85)",
                      }}
                    />
                    {isUserMgmtOpen ? (
                      <ExpandLess sx={{ color: "rgba(255,255,255,0.6)" }} />
                    ) : (
                      <ExpandMore sx={{ color: "rgba(255,255,255,0.6)" }} />
                    )}
                  </>
                )}
              </ListItem>

              {!miniSidenav && (
                <Collapse in={isUserMgmtOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {children.map((child) => {
                      const isChildActive = pathname === child.route;
                      return (
                        <NavLink
                          key={child.key}
                          to={child.route}
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          <ListItem
                            button
                            sx={{
                              minHeight: 40,
                              pl: 4,
                              margin: "2px 10px",
                              borderRadius: "10px",
                              backgroundColor: isChildActive
                                ? "rgba(255,255,255,0.12)"
                                : "transparent",
                              "&:hover": {
                                backgroundColor: "rgba(255,255,255,0.08)",
                                transform: "translateX(4px)",
                                transition: "all 0.3s ease",
                              },
                              transition: "all 0.3s ease",
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 36,
                                color: isChildActive
                                  ? "#ffffff"
                                  : "rgba(255,255,255,0.6)",
                              }}
                            >
                              {child.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={child.name}
                              primaryTypographyProps={{
                                fontSize: "0.875rem",
                                fontWeight: isChildActive ? 600 : 400,
                                color: isChildActive
                                  ? "#ffffff"
                                  : "rgba(255,255,255,0.75)",
                              }}
                            />
                          </ListItem>
                        </NavLink>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        } else {
          const isActive = pathname === route;
          return (
            <NavLink
              key={key}
              to={route}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <ListItem
                button
                sx={{
                  minHeight: 48,
                  borderRadius: "12px",
                  margin: "4px 10px",
                  padding: "10px 14px",
                  backgroundColor: isActive
                    ? "rgba(255,255,255,0.15)"
                    : "transparent",
                  color: isActive ? "#ffffff" : "rgba(255,255,255,0.8)",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                    transition: "all 0.3s ease",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? "#ffffff" : "rgba(255,255,255,0.7)",
                    minWidth: miniSidenav ? "auto" : 40,
                  }}
                >
                  {icon}
                </ListItemIcon>
                {!miniSidenav && (
                  <ListItemText
                    primary={name}
                    primaryTypographyProps={{
                      fontSize: "0.875rem",
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? "#ffffff" : "rgba(255,255,255,0.85)",
                    }}
                  />
                )}
              </ListItem>
            </NavLink>
          );
        }
      }
      return null;
    },
  );

  return (
    <SidebarRoot
      {...rest}
      variant="permanent"
      ownerState={{
        miniSidenav,
        darkMode,
      }}
    >
      {/* Brand Header with Gradient */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: gradientBackground,
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          minHeight: "60px",
        }}
      >
        {!miniSidenav && (
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              color: "#b1b1b1",
              opacity: miniSidenav ? 0 : 1,
              transition: "opacity 0.2s ease",
              whiteSpace: "nowrap",
              overflow: "hidden",
              fontSize: "18px",
              letterSpacing: "0.5px",
              textShadow: "0 5px 8px rgba(0, 0, 0, 0.51)",
            }}
          >
            {brandName}
          </Typography>
        )}

        <Box sx={{ display: "flex", gap: 0.5 }}>
          {/* Toggle Sidebar Button */}
          <IconButton
            size="small"
            onClick={() => setMiniSidenav(!miniSidenav)}
            sx={{
              color: "rgba(255,255,255,0.8)",
              backgroundColor: "rgba(255,255,255,0.1)",
              borderRadius: "10px",
              padding: "8px",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.2)",
                transform: "scale(1.05)",
              },
              transition: "all 0.3s ease",
            }}
          >
            {miniSidenav ? (
              <MenuIcon fontSize="small" />
            ) : (
              <MenuOpenIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      </Box>

      <Divider
        sx={{
          borderColor: "rgba(255,255,255,0.08)",
        }}
      />

      <List sx={{ mt: 1, px: 0 }}>{renderRoutes}</List>
    </SidebarRoot>
  );
}

Sidebar.propTypes = {
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  miniSidenav: PropTypes.bool,
  setMiniSidenav: PropTypes.func,
  darkMode: PropTypes.bool,
  setDarkMode: PropTypes.func,
};
